import "@/App.css";
import { Router, Route, Switch } from "wouter";
import { useHashLocation as wouterUseHashLocation } from "wouter/use-hash-location";
import { UserProvider } from "@/lib/user";
import LandingPage from "@/pages/LandingPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import QuestPage from "@/pages/QuestPage";
import ScoreCardPage from "@/pages/ScoreCardPage";
import PaywallPage from "@/pages/PaywallPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import RefundPage from "@/pages/RefundPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import AdminPage from "@/pages/AdminPage";
import SqueezePage from "@/pages/SqueezePage";
import Footer from "@/components/Footer";
import { Toaster } from "sonner";

// Wrap wouter's useHashLocation so that any `?query` after the hash path is
// stripped before Wouter does its route matching. This lets us accept
// Stripe-style redirects like `/#/payment-success?session_id=cs_test_xxx`
// while still matching the route `path="/payment-success"`. The raw query
// stays in window.location.hash, so PaymentSuccessPage can parse session_id.
function useHashLocation() {
  const [loc, nav] = wouterUseHashLocation();
  const q = loc.indexOf("?");
  return [q >= 0 ? loc.slice(0, q) : loc, nav];
}

function NotFound() {
  return (
    <main data-testid="notfound-page" className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-300">404</p>
        <h1 className="mt-2 text-2xl font-semibold text-zinc-100">Lost the path.</h1>
        <a href="#/" className="mt-4 inline-block text-sm text-amber-300 hover:text-amber-200">
          ← back to landing
        </a>
      </div>
    </main>
  );
}

function App() {
  return (
    <UserProvider>
      <Router hook={useHashLocation}>
        <div className="flex flex-col min-h-screen">
          <div className="flex-1 flex flex-col">
            <Switch>
              <Route path="/" component={LandingPage} />
              <Route path="/onboarding" component={OnboardingPage} />
              <Route path="/dashboard" component={DashboardPage} />
              <Route path="/quest/:id">
                {(params) => <QuestPage params={params} />}
              </Route>
              <Route path="/scorecard/:questId">
                {(params) => <ScoreCardPage params={params} />}
              </Route>
              <Route path="/paywall" component={PaywallPage} />
              <Route path="/payment-success" component={PaymentSuccessPage} />
              <Route path="/leaderboard" component={LeaderboardPage} />
              <Route path="/terms" component={TermsPage} />
              <Route path="/privacy" component={PrivacyPage} />
              <Route path="/refund" component={RefundPage} />
              <Route path="/about" component={AboutPage} />
              <Route path="/contact" component={ContactPage} />
              <Route path="/admin" component={AdminPage} />
              <Route path="/start" component={SqueezePage} />
              <Route component={NotFound} />
            </Switch>
          </div>
          {/* Razorpay compliance: footer with legal links is reachable from every page. */}
          <Footer />
          {/* Global toast host — used by AdminPage nudge feedback etc. */}
          <Toaster position="top-right" richColors theme="dark" />
        </div>
      </Router>
    </UserProvider>
  );
}

export default App;
